import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";

/**
 * AdminUsers — subscription directory pulled from Razorpay records.
 *
 * NOT a full user directory (Fertile-Track owns those). This lists every
 * distinct ft_user_id that has EVER interacted with Femcare's billing
 * layer, with their current active subscription tier derived from
 * /api/web/admin/users which uses the SAME _confirmed_query() as the
 * Web Conversions dashboard — so "Pro Monthly" here always agrees with
 * a matching filter in /admin/conversions.
 */
const fmtIST = (v) => {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleString("en-IN", {
            dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata",
        }) + " IST";
    } catch { return String(v); }
};

const tierBadge = (t) => {
    const map = {
        Free:     "bg-femcare-ink/5 text-femcare-ink/60",
        monthly:  "bg-emerald-50 text-emerald-800",
        yearly:   "bg-amber-50 text-amber-800",
        lifetime: "bg-femcare-plum/10 text-femcare-plum",
        unknown:  "bg-femcare-ink/5 text-femcare-ink/50",
    };
    const label = t === "Free" ? "Free"
                : t === "monthly"  ? "Pro · Monthly"
                : t === "yearly"   ? "Pro · Yearly"
                : t === "lifetime" ? "Pro · Lifetime"
                : "—";
    return { cls: map[t] || map.unknown, label };
};

export default function AdminUsers() {
    const nav = useNavigate();
    const { user, accessToken } = useAuth();
    const [rows, setRows] = useState([]);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        (async () => {
            try {
                const API = process.env.REACT_APP_BACKEND_URL;
                const r = await fetch(`${API}/api/web/admin/users?limit=500`, {
                    credentials: "include",
                    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
                });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const data = await r.json();
                setRows(data.rows || []);
            } catch (e) { setErr(e.message); }
        })();
    }, [user, accessToken, nav]);

    if (!user || user.role !== "admin") return null;

    return (
        <div className="min-h-screen bg-femcare-cream/40 py-10" data-testid="admin-users-page">
            <div className="max-w-6xl mx-auto px-6 space-y-6">
                <header>
                    <div className="text-xs uppercase tracking-widest text-femcare-plum font-semibold">Admin</div>
                    <h1 className="text-2xl font-bold text-femcare-ink mt-1">User Subscriptions</h1>
                    <p className="text-xs text-femcare-ink/55 mt-1">
                        Users who have interacted with Femcare billing. Active plan is derived from Razorpay-captured payments.
                        Free-tier users who never entered checkout live in Fertile-Track and are not enumerable here.
                    </p>
                    <div className="flex gap-3 mt-2 text-xs">
                        <Link to="/admin" className="text-femcare-plum hover:underline">Dashboard</Link>
                        <Link to="/admin/conversions" className="text-femcare-plum hover:underline">Conversions</Link>
                        <Link to="/admin/publishers" className="text-femcare-plum hover:underline">Publishers</Link>
                        <Link to="/admin/publisher-links" className="text-femcare-plum hover:underline">Publisher Links</Link>
                    </div>
                </header>

                {err && <div className="rounded-xl bg-red-50 text-red-800 px-4 py-3" data-testid="admin-users-error">{err}</div>}

                <div className="bg-white rounded-2xl border border-femcare-border overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-users-table">
                        <thead className="bg-femcare-cream/40 text-femcare-ink/70">
                            <tr className="text-left text-xs uppercase tracking-wider">
                                <th className="px-4 py-3">FT User ID</th>
                                <th className="px-4 py-3">Active Plan</th>
                                <th className="px-4 py-3">Plan ID</th>
                                <th className="px-4 py-3">Confirmed At</th>
                                <th className="px-4 py-3">Total Confirmed</th>
                                <th className="px-4 py-3">Currency</th>
                                <th className="px-4 py-3">Last Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-8 text-center text-femcare-ink/50" data-testid="admin-users-empty">
                                    No user billing activity yet.
                                </td></tr>
                            )}
                            {rows.map((r) => {
                                const badge = tierBadge(r.active_plan);
                                return (
                                    <tr key={r.ft_user_id} className="border-t border-femcare-border" data-testid={`admin-user-row-${r.ft_user_id}`}>
                                        <td className="px-4 py-3 text-xs font-mono">{r.ft_user_id}</td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-semibold rounded-full px-2 py-1 ${badge.cls}`}
                                                  data-testid={`admin-user-plan-${r.ft_user_id}`}>
                                                {badge.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs font-mono">{r.active_plan_id || "—"}</td>
                                        <td className="px-4 py-3 text-xs">{fmtIST(r.confirmed_at)}</td>
                                        <td className="px-4 py-3 text-xs font-semibold">{r.total_confirmed}</td>
                                        <td className="px-4 py-3 text-xs">{r.currency || "—"}</td>
                                        <td className="px-4 py-3 text-xs">{fmtIST(r.last_activity_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
